import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle,
  Sparkle,
  Lightning,
  ArrowRight,
  ShieldCheck,
  Infinity as InfinityIcon,
  Gift,
} from "@phosphor-icons/react";
import {
  fetchPricingContext,
  buyCreditPack,
  validatePromoCode,
  redeemPromoCredits,
} from "../lib/billing";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import PeachCheckoutModal from "../components/PeachCheckoutModal";

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [balance, setBalance] = useState(null);
  const [pendingCode, setPendingCode] = useState("");
  const [applied, setApplied] = useState(null); // { code, perPack: { [packId]: validateResult } }
  const [busy, setBusy] = useState(false);
  const [peachOpen, setPeachOpen] = useState(false);
  const [peachEmbed, setPeachEmbed] = useState(null);
  const [peachLabel, setPeachLabel] = useState({ plan: "", amount: "" });
  const [peachFeatures, setPeachFeatures] = useState([]);
  const [peachPromo, setPeachPromo] = useState(null);
  const [peachTxn, setPeachTxn] = useState(null);
  const promoInputRef = useRef(null);

  const loadBalance = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/usage/me");
      setBalance(data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setCtx(await fetchPricingContext());
      } catch (e) {
        toast.error("Could not load pricing");
        console.error(e);
      }
    })();
  }, []);

  useEffect(() => {
    loadBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const packPriceMap = useMemo(() => {
    if (!ctx) return {};
    const m = {};
    (ctx.pack_prices || []).forEach((p) => (m[p.pack_id] = p));
    return m;
  }, [ctx]);

  const bundle = ctx?.bundle && ctx.bundle.active ? ctx.bundle : null;

  // One input, smart behaviour: try a standalone credit-grant redeem first,
  // and if the code is a checkout discount instead, apply it to purchases.
  const handleApplyCode = async () => {
    const code = pendingCode.trim().toUpperCase();
    if (!code) {
      toast.error("Enter a promo code first.");
      return;
    }
    setBusy(true);
    try {
      // 1) Standalone credit grant?
      try {
        const res = await redeemPromoCredits(code);
        if (res?.ok) {
          toast.success(`${res.credits_granted} conversions added to your account!`);
          setPendingCode("");
          setApplied(null);
          await loadBalance();
          return;
        }
      } catch (err) {
        const detail = err.response?.data?.detail || "";
        // Not a grant code → fall through to discount validation.
        if (err.response?.status === 401) {
          toast.error("Sign in to redeem a code.");
          return;
        }
        if (err.response?.status === 409) {
          toast.error(typeof detail === "string" ? detail : "Already redeemed.");
          return;
        }
        if (err.response?.status !== 400 && err.response?.status !== 404) {
          toast.error(typeof detail === "string" ? detail : "Could not redeem code.");
          return;
        }
      }
      // 2) Discount code applied at checkout — validate against EVERY
      // current pack (not just one hardcoded pack) so a code scoped to
      // specific packs (e.g. Starter only) reads correctly no matter which
      // pack the shopper ends up buying.
      const packIds = (ctx?.credit_packs || []).map((p) => p.id);
      const results = await Promise.all(
        packIds.map((packId) =>
          validatePromoCode({ code, kind: "pack", packId }).catch(() => ({ ok: false, error: "Could not check this pack." })),
        ),
      );
      const perPack = {};
      packIds.forEach((packId, i) => { perPack[packId] = results[i]; });
      const eligiblePackIds = packIds.filter((id) => perPack[id].ok);
      if (eligiblePackIds.length === 0) {
        const firstError = packIds.map((id) => perPack[id].error).find(Boolean);
        toast.error(firstError || "Invalid or expired code.");
        setApplied(null);
        return;
      }
      setApplied({ code, perPack });
      const allPacks = eligiblePackIds.length === packIds.length;
      const eligibleNames = eligiblePackIds
        .map((id) => ctx.credit_packs.find((p) => p.id === id)?.name)
        .filter(Boolean)
        .join(", ");
      const anyFree = eligiblePackIds.some((id) => perPack[id].free);
      toast.success(
        `${code} applied — ${anyFree ? "free" : "discounted"} on ${allPacks ? "every pack" : eligibleNames}.`,
      );
    } finally {
      setBusy(false);
    }
  };

  const handleBuyPack = async (packId) => {
    if (!user) {
      toast.info("Create a free account to buy a pack.");
      navigate("/login?next=/pricing");
      return;
    }
    const promoForPack = applied?.perPack?.[packId];
    if (applied && !promoForPack?.ok) {
      toast.info(`${applied.code} doesn't apply to this pack — buying at full price.`);
    }
    const code = promoForPack?.ok ? applied.code : null;
    setBusy(true);
    try {
      const data = await buyCreditPack(packId, undefined, code);
      const pack = ctx.credit_packs.find((p) => p.id === packId);
      const convs = pack?.conversions ?? pack?.credits ?? 0;
      const bonus = bundle && bundle.pack_id === packId ? bundle.bonus_credits : 0;
      // Free (100% promo) → straight to confirmation.
      if (data.free || data.is_free) {
        await loadBalance();
        navigate(`/checkout/callback?txn_id=${data.txn_id}&free=1`);
        return;
      }
      if (data.peach_embed && data.peach_embed.checkout_id) {
        setPeachEmbed(data.peach_embed);
        // Use the ACTUAL server response (data.amount / data.original_amount),
        // not the earlier /promo/validate preview — it's the authoritative
        // amount the checkout_id was actually created for.
        const finalFmt = `R${Number(data.amount).toFixed(2)}`;
        setPeachLabel({
          plan: `${pack?.name || "Pack"} — ${convs} conversions`,
          amount: finalFmt,
        });
        setPeachFeatures([
          `${convs} conversions added instantly`,
          bonus ? `+ ${bonus} bonus conversions (promo)` : "One-time purchase — not a subscription",
          "Conversions never expire",
        ]);
        setPeachPromo(
          data.promo_applied
            ? {
                code: data.promo_applied,
                originalLabel: `R${Number(data.original_amount).toFixed(2)}`,
                finalLabel: finalFmt,
                discountLabel: `R${Number(data.discount_usd).toFixed(2)} off`,
              }
            : null,
        );
        setPeachTxn(data.txn_id);
        setPeachOpen(true);
        return;
      }
      navigate(
        `/checkout/callback?txn_id=${data.txn_id}` +
          (data.is_mock ? "&mock=1" : "") +
          (data.redirect_url ? `&gw=${encodeURIComponent(data.redirect_url)}` : ""),
      );
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "";
      toast.error(typeof msg === "string" ? msg : "Could not start checkout.");
    } finally {
      setBusy(false);
    }
  };

  if (!ctx) {
    return (
      <div className="py-32 text-center text-[var(--fg-2)] font-mono text-xs tracking-widest">
        LOADING PRICING…
      </div>
    );
  }

  const packs = ctx.credit_packs || [];

  return (
    <div className="max-w-[1440px] mx-auto" data-testid="pricing-page">
      {/* Hero */}
      <section className="border-b border-[var(--border)]">
        <div className="px-6 md:px-10 py-12 md:py-20">
          <div className="micro-label flex items-center gap-2">
            <Sparkle size={12} weight="bold" /> / Pricing · ZAR · Pay per pack
          </div>
          <h1 className="font-display font-black tracking-tight leading-[0.95] text-5xl md:text-6xl lg:text-7xl mt-3">
            Buy once.
            <br />
            <span className="text-[var(--accent)]">Convert</span> anytime.
          </h1>
          <p className="mt-8 text-[var(--fg-2)] max-w-xl">
            No subscriptions. No recurring billing. Buy a conversion pack once —
            <span className="font-mono"> 1 credit = 1 conversion</span>, and paid credits{" "}
            <strong>never expire</strong>. Every account also gets{" "}
            <span className="font-mono">{ctx.free_monthly ?? 2} free conversions each month</span>.
          </p>
          {user && balance && (
            <div
              className="mt-8 inline-flex flex-wrap items-center gap-4 hairline bg-white px-5 py-3"
              data-testid="pricing-balance-strip"
            >
              <span className="micro-label">/ Your balance</span>
              <span className="font-display font-black text-2xl" data-testid="pricing-balance-total">
                {balance.unlimited ? "∞" : balance.conversions_remaining ?? 0}
              </span>
              <span className="text-xs font-mono text-[var(--fg-2)]">
                {balance.unlimited
                  ? "ADMIN — UNLIMITED"
                  : `conversions left · ${balance.free_credits ?? 0} free + ${balance.paid_credits ?? 0} paid`}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Bundle promo banner */}
      {bundle && (
        <section
          className="border-b border-[var(--border)] bg-[var(--accent)] text-white"
          data-testid="bundle-banner"
        >
          <div className="px-6 md:px-10 py-4 flex items-center gap-3">
            <Gift size={20} weight="fill" />
            <span className="font-mono text-sm tracking-wide">
              {bundle.label ||
                `Buy the ${packs.find((p) => p.id === bundle.pack_id)?.name || ""} pack → get ${bundle.bonus_credits} bonus conversions free!`}
            </span>
          </div>
        </section>
      )}

      {/* Promo code */}
      <section className="border-b border-[var(--border)] bg-[var(--bg-2)]" data-testid="promo-section">
        <div className="px-6 md:px-10 py-6 flex flex-col md:flex-row items-stretch md:items-center gap-3">
          <div className="micro-label flex-shrink-0">/ Have a promo code?</div>
          <input
            ref={promoInputRef}
            type="text"
            value={pendingCode}
            onChange={(e) => {
              setPendingCode(e.target.value);
              if (applied) setApplied(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleApplyCode();
              }
            }}
            placeholder="Enter code"
            className="flex-1 md:max-w-xs px-3 py-2 border border-[var(--border)] bg-white font-mono uppercase text-sm focus:outline-none focus:border-[var(--accent)]"
            data-testid="promo-code-input"
            autoCapitalize="characters"
            spellCheck={false}
          />
          <button
            type="button"
            onClick={handleApplyCode}
            disabled={!pendingCode.trim() || busy}
            data-testid="promo-apply-btn"
            className="px-5 py-2 font-mono text-xs uppercase tracking-wider border-2 border-[var(--accent)] bg-[var(--accent)] text-white hover:bg-white hover:text-[var(--accent)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? "Checking…" : "Apply"}
          </button>
          {applied ? (
            <div
              className="text-xs font-mono tracking-wider px-3 py-2 border border-[var(--accent)] text-[var(--accent)] bg-white"
              data-testid="promo-applied-badge"
            >
              {applied.code} applied — check packs below for your price
            </div>
          ) : (
            <p className="text-xs text-[var(--fg-2)] md:max-w-md" data-testid="promo-hint">
              Codes may add free conversions instantly, or discount a pack at checkout.
            </p>
          )}
        </div>
      </section>

      {/* Pack grid */}
      <section className="border-b border-[var(--border)]">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border)] border-y border-[var(--border)]">
          {packs.map((pk) => {
            const pr = packPriceMap[pk.id];
            const convs = pk.conversions ?? pk.credits;
            const bonus = bundle && bundle.pack_id === pk.id ? bundle.bonus_credits : 0;
            const promoForPack = applied?.perPack?.[pk.id];
            const discountLabel = promoForPack?.ok
              ? (promoForPack.free ? "FREE" : `R${promoForPack.final_usd.toFixed(2)}`)
              : null;
            return (
              <PackCard
                key={pk.id}
                name={pk.name}
                conversions={convs}
                bonus={bonus}
                priceLabel={pr?.display_formatted ?? `R${pk.price_zar}`}
                discountLabel={discountLabel}
                perConv={pr ? (pk.price_zar / convs) : pk.price_zar / convs}
                highlight={pk.highlight}
                testid={`pack-${pk.id}`}
                disabled={busy}
                onBuy={() => handleBuyPack(pk.id)}
              />
            );
          })}
        </div>
      </section>

      {/* Reassurance */}
      <section className="border-t border-[var(--border)]">
        <div className="px-6 md:px-10 py-16 grid md:grid-cols-3 gap-8">
          <Assurance
            icon={<ShieldCheck size={20} weight="fill" />}
            title="Secure checkout"
            body="POPIA-compliant hosted checkout via Peach Payments — we never see your card."
          />
          <Assurance
            icon={<InfinityIcon size={20} weight="bold" />}
            title="Paid credits never expire"
            body="Buy a pack once and use your conversions whenever you like. No monthly commitment."
          />
          <Assurance
            icon={<Sparkle size={20} weight="fill" />}
            title="2 free every month"
            body={`Every account gets ${ctx.free_monthly ?? 2} free conversions each month, reset on the 1st. Failed runs cost nothing.`}
          />
        </div>
      </section>

      <PeachCheckoutModal
        open={peachOpen}
        embed={peachEmbed}
        planLabel={peachLabel.plan}
        amountLabel={peachLabel.amount}
        features={peachFeatures}
        promo={peachPromo}
        onCompleted={() => {
          setPeachOpen(false);
          loadBalance();
          navigate(`/checkout/callback?txn_id=${peachTxn}&peach=1&status=success`);
        }}
        onCancelled={() => {
          setPeachOpen(false);
          toast.info("Checkout cancelled — no charge was made.");
        }}
        onExpired={() => {
          setPeachOpen(false);
          toast.error("Checkout session expired. Try again when you're ready.");
        }}
        onClose={() => setPeachOpen(false)}
      />
    </div>
  );
}

// ---------- building blocks ----------
function PackCard({ name, conversions, bonus, priceLabel, discountLabel, perConv, highlight, testid, onBuy, disabled }) {
  return (
    <div
      className={`bg-white relative p-6 md:p-8 flex flex-col transition-transform ${
        highlight ? "shadow-brutal-accent z-10" : "hover:-translate-y-1 hover:shadow-brutal"
      }`}
      data-testid={testid}
    >
      {highlight && (
        <span className="panel-tab" style={{ background: "var(--accent)" }}>
          <Sparkle size={10} weight="fill" className="inline mr-1" />
          BEST VALUE
        </span>
      )}
      <div className="flex items-center justify-between mt-2">
        <div className="h-10 w-10 border-2 border-black flex items-center justify-center">
          <Lightning size={20} weight="bold" />
        </div>
        <span className="chip">ONE-TIME</span>
      </div>
      <div className="mt-7">
        <div className="font-display font-bold text-2xl">{name}</div>
        {discountLabel ? (
          <div className="mt-3 flex items-baseline gap-2 flex-wrap" data-testid={`${testid}-discounted`}>
            <span className="line-through text-xl text-[var(--fg-2)] font-mono">{priceLabel}</span>
            <span className="font-display font-black text-5xl tracking-tight leading-none text-[var(--accent)]">
              {discountLabel}
            </span>
          </div>
        ) : (
          <div className="font-display font-black text-5xl tracking-tight mt-3 leading-none">
            {priceLabel}
          </div>
        )}
        <div className="text-xs font-mono text-[var(--fg-2)] tracking-wider mt-2">
          one-time · billed in ZAR
        </div>
      </div>
      <ul className="mt-7 space-y-2.5 text-sm flex-1">
        <li className="flex items-start gap-2">
          <CheckCircle size={16} weight="fill" className="text-[var(--success)] mt-0.5 shrink-0" />
          <span className="font-mono">
            <strong>{conversions}</strong> conversions
          </span>
        </li>
        {bonus > 0 && (
          <li className="flex items-start gap-2 text-[var(--accent)]" data-testid={`${testid}-bonus`}>
            <Gift size={16} weight="fill" className="mt-0.5 shrink-0" />
            <span className="font-mono">+ {bonus} bonus (promo)</span>
          </li>
        )}
        <li className="flex items-start gap-2">
          <CheckCircle size={16} weight="fill" className="text-[var(--success)] mt-0.5 shrink-0" />
          <span>Never expire</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle size={16} weight="fill" className="text-[var(--success)] mt-0.5 shrink-0" />
          <span className="text-[var(--fg-2)]">≈ R{perConv.toFixed(2)} / conversion</span>
        </li>
      </ul>
      <button
        onClick={onBuy}
        disabled={disabled}
        className={`mt-8 w-full ${highlight ? "btn-accent" : "btn-primary"} justify-center disabled:opacity-50 disabled:cursor-not-allowed`}
        data-testid={`${testid}-buy`}
      >
        Buy pack <ArrowRight size={14} weight="bold" />
      </button>
    </div>
  );
}

function Assurance({ icon, title, body }) {
  return (
    <div className="hairline bg-white p-6">
      <div className="h-10 w-10 border border-black flex items-center justify-center">{icon}</div>
      <div className="font-display font-bold text-lg mt-5">{title}</div>
      <div className="text-sm text-[var(--fg-2)] mt-2 leading-relaxed">{body}</div>
    </div>
  );
}
